import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp, library } from "@fortawesome/fontawesome-svg-core";
import {
  faHouse,
  faPeopleGroup,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons";

// Register required icons in the library so tuple references ["fas", name] resolve & display
// Cast to any to bridge potential version typing mismatch between core 6.x and icon 7.x packages.
library.add(faHouse as any, faPeopleGroup as any, faUtensils as any);
import "./styles.scss";

// Use tuple IconProp form instead of byPrefixAndName macro to avoid macro/build issues and version mismatches.
// Ensure the icon names exist in the installed free solid set. Adjust names if necessary.
interface NavItem {
  title: string;
  icon: IconProp;
  href: string;
  colorClass: string; // text / accent color utilities
  borderClass: string; // border utilities for icon box
  bgClass: string; // bg utilities for icon box
}

const NAV_ITEMS: NavItem[] = [
  {
    title: "Home",
    icon: ["fas", "house"],
    href: "/",
    colorClass: "text-sow-gold",
    borderClass: "border-sow-gold/40",
    bgClass: "bg-sow-gold/10",
  },
  {
    title: "About Us",
    icon: ["fas", "people-group"],
    href: "/about",
    colorClass: "text-sow-white",
    borderClass: "border-sow-white/30",
    bgClass: "bg-sow-white/5",
  },
  {
    title: "Menu",
    icon: ["fas", "utensils"],
    href: "/menu",
    colorClass: "text-sow-darkGold",
    borderClass: "border-sow-darkGold/40",
    bgClass: "bg-sow-darkGold/10",
  },
];

export interface NavProps {
  expandedWidth?: number; // px width when expanded (default 300)
  collapsedWidth?: number; // px width when collapsed (default 60 ~ w-15)
  sticky?: boolean; // if true uses position:fixed
  top?: number; // top offset when sticky
  left?: number; // left offset when sticky
  elevated?: boolean; // adds ring / stronger shadow
  variant?: "expand" | "isometric"; // choose style
}

export const NavigationPiece: React.FC<
  NavProps & { links?: { title: string; href: string; visible: boolean }[] }
> = ({
  links,
  expandedWidth = 300,
  collapsedWidth = 60,
  sticky = false,
  top = 24,
  left = 24,
  elevated = true,
  variant = "expand",
}) => {
  const navItems = links
    ? links
        .filter((link) => link.visible)
        .map((link, index) => ({
          ...NAV_ITEMS[index % NAV_ITEMS.length],
          ...link,
        }))
    : NAV_ITEMS;
  const styleVars: React.CSSProperties = {
    ["--nav-expanded-width" as any]: `${expandedWidth}px`,
    ["--nav-collapsed-width" as any]: `${collapsedWidth}px`,
  };
  const positionStyle: React.CSSProperties = sticky
    ? { position: "fixed", top, left, zIndex: 50 }
    : {};
  if (variant === "isometric") {
    return (
      <div
        className={`max-w-full sm:max-w-fit rounded-xl sm:p-4 p-2 backdrop-blur-xl bg-[#231f1f]/70 transition duration-500 hover:bg-[#2d2828]/70 flex ${
          elevated
            ? "shadow-[inset_0_0_20px_rgba(255,255,255,0.15),inset_0_0_6px_rgba(255,255,255,0.25),0_6px_14px_-4px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
            : ""
        }`}
        style={{ ...positionStyle }}
      >
        <ul className="flex flex-row sm:flex-col items-center justify-center gap-3 sm:gap-6 w-full">
          {navItems.map((item) => (
            <li
              key={item.title}
              className="group relative iso-pro cursor-pointer"
            >
              {/* layered glow rings */}
              <span className="pointer-events-none absolute inset-0 rounded-full opacity-0 shadow-[inset_0_0_18px_rgba(255,255,255,0.25),inset_0_0_5px_rgba(255,255,255,0.4),0_5px_8px_-2px_rgba(0,0,0,0.45)] transition-all duration-300 group-hover:opacity-20" />
              <span className="pointer-events-none absolute inset-0 rounded-full opacity-0 shadow-[inset_0_0_18px_rgba(255,255,255,0.25),inset_0_0_5px_rgba(255,255,255,0.4),0_5px_8px_-2px_rgba(0,0,0,0.45)] transition-all duration-300 group-hover:opacity-40 group-hover:translate-x-[5px] group-hover:-translate-y-[5px]" />
              <span className="pointer-events-none absolute inset-0 rounded-full opacity-0 shadow-[inset_0_0_18px_rgba(255,255,255,0.25),inset_0_0_5px_rgba(255,255,255,0.4),0_5px_8px_-2px_rgba(0,0,0,0.45)] transition-all duration-300 group-hover:opacity-60 group-hover:translate-x-[10px] group-hover:-translate-y-[10px]" />
              <a
                href={item.href}
                aria-label={item.title}
                className={`relative block h-14 w-14 sm:h-[72px] sm:w-[72px] rounded-full p-3 sm:p-4 text-center transition-all duration-300 active:scale-[0.95] shadow-[inset_0_0_18px_rgba(255,255,255,0.3),inset_0_0_5px_rgba(255,255,255,0.5),0_5px_5px_rgba(0,0,0,0.16)] bg-black/50 backdrop-blur-xl ${item.colorClass} group-hover:translate-x-[5px] group-hover:-translate-y-[5px]`}
              >
                <div className="h-full w-full flex items-center justify-center transition-transform duration-300 group-hover:translate-x-[15px] group-hover:-translate-y-[15px]">
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="h-6 w-6 sm:h-8 sm:w-8"
                  />
                </div>
              </a>
              {/* Mobile inline label */}
              <div className="mt-1 text-[10px] font-semibold text-sow-gold sm:hidden text-center select-none">
                {item.title}
              </div>
              {/* Tooltip (desktop only) */}
              <div className="hidden sm:block absolute left-full top-1/2 ml-4 -translate-y-1/2 whitespace-nowrap rounded-md bg-white/30 px-2 py-1 text-xs font-semibold text-sow-gold opacity-0 shadow-[ -5px_0_1px_rgba(153,153,153,0.25),-10px_0_1px_rgba(153,153,153,0.25),inset_0_0_18px_rgba(255,255,255,0.3),inset_0_0_4px_rgba(255,255,255,0.45),0_4px_6px_rgba(0,0,0,0.25)] backdrop-blur-sm transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1 group-hover:skew-x-[-5deg] group-focus-within:opacity-100 group-focus-within:translate-x-1 group-focus-within:skew-x-[-5deg]">
                {item.title}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // default 'expand' variant
  return (
    <ul
      className={`sow-expand-nav flex flex-row sm:flex-col gap-1 sm:pl-1 pl-0 border-l sm:border-white/10 border-transparent rounded-xl backdrop-blur-xl bg-[#231f1f]/70 ${
        elevated
          ? "shadow-[0_4px_16px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
          : ""
      }`}
      style={{ ...styleVars, ...positionStyle }}
    >
      {navItems.map((item) => (
        <li
          key={item.title}
          className="group relative rounded-lg border-l border-transparent bg-white/10/40 transition-all duration-500 shadow-[0_1px_2px_rgba(0,0,0,0.4)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.45)] focus-within:shadow-[0_4px_12px_rgba(0,0,0,0.45)] flex flex-col sm:block"
          style={{ width: "var(--nav-collapsed-width)" }}
        >
          <a
            href={item.href}
            className={`peer flex w-full items-center sm:items-center gap-2 sm:gap-3 pl-3 pr-3 sm:pr-4 py-2 text-left transition-all active:scale-[0.97] ${item.colorClass}`}
            aria-label={item.title}
          >
            <div
              className={`rounded-lg border-2 ${item.borderClass} ${item.bgClass} p-2 flex items-center justify-center transition-colors`}
            >
              <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
            </div>
            <span className="hidden sm:inline hammer font-semibold whitespace-nowrap opacity-0 translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 peer-focus:opacity-100 peer-focus:translate-x-0 text-sow-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
              {item.title}
            </span>
          </a>
          {/* Mobile label below for expand variant */}
          <span className="sm:hidden block text-center text-[10px] pb-2 -mt-1 font-semibold text-sow-gold">
            {item.title}
          </span>
          <style>
            {`
              .sow-expand-nav li:hover, .sow-expand-nav li:focus-within { width: var(--nav-expanded-width); }
            `}
          </style>
        </li>
      ))}
    </ul>
  );
};
